import { useState } from "react";
import { KeyListItem, WorkspaceKeyType } from "@optidev/shared-types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { Check, Eye, EyeOff, Loader2 } from "lucide-react";
import { auth } from "@/lib/firebase";
import { workspaceKeysApi } from "@/lib/api/workspace-keys";
import toast from "react-hot-toast";

interface WorkspaceKeyCardProps {
  keyData: KeyListItem;
  onRotate: (keyId: string, version: number) => void;
  onRevoke: (keyId: string) => void;
  isAdmin: boolean;
}

export default function WorkspaceKeyCard({
  keyData,
  onRotate,
  onRevoke,
  isAdmin,
}: WorkspaceKeyCardProps) {
  const [showSecret, setShowSecret] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [loadingSecret, setLoadingSecret] = useState(false);

  const isConnectionKey = keyData.keyType === WorkspaceKeyType.CONNECTION;
  const keyTitle = isConnectionKey ? "Connection Key" : "Content Signing Key";

  const handleToggleSecret = async () => {
    if (showSecret) {
      // Hide secret
      setShowSecret(false);
      return;
    }

    // Show secret - fetch if not already loaded
    if (!secret) {
      setLoadingSecret(true);
      try {
        const token = await auth.getIdToken();
        if (!token) {
          toast.error("Authentication required");
          return;
        }

        const response = await workspaceKeysApi.getSecret(token, keyData.keyId);
        setSecret(response.secret);
        setShowSecret(true);
      } catch (error: any) {
        console.error("Failed to fetch secret:", error);
        toast.error(
          error.response?.data?.message || "Failed to retrieve secret"
        );
      } finally {
        setLoadingSecret(false);
      }
    } else {
      setShowSecret(true);
    }
  };

  const handleCopySecret = async () => {
    if (!secret) return;

    try {
      await navigator.clipboard.writeText(secret);
      toast.success("Secret copied to clipboard");
    } catch (error) {
      console.error("Failed to copy secret:", error);
      toast.error("Failed to copy secret");
    }
  };

  const formatDate = (date?: Date): string => {
    if (!date) return "Never";
    const d = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - d.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMonths > 0) {
      return `Created ${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
    } else if (diffDays > 0) {
      return `Created ${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    } else {
      return "Created today";
    }
  };

  return (
    <Card className="p-6 border-border/40">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-lg font-semibold">{keyTitle}</h3>
        </div>
        {keyData.isActive ? (
          <Badge variant="outline" className="text-muted-foreground">
            <Check size={14} className="mr-1" /> Active
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Revoked
          </Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {formatDate(keyData.createdAt)}
      </p>

      {/* Key ID */}
      <div className="mb-4">
        <p className="text-xs font-medium text-muted-foreground mb-2">KEY ID</p>
        <div className="relative bg-muted px-3 py-2 rounded-lg border border-border/40">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm text-foreground">
              {keyData.keyId}
            </span>
            <CopyableCell
              value={keyData.keyId}
              display=""
              tooltipLabel="Copy key ID"
              iconSize={14}
              iconClassName="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>
      </div>

      {/* Secret */}
      {isAdmin && keyData.isActive && (
        <div className="mb-6">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            SECRET
          </p>
          <div className="relative">
            <code className="block text-sm font-mono text-foreground bg-muted px-3 py-2 pr-20 rounded-lg border border-border/40 break-all">
              {showSecret && secret
                ? secret
                : "••••••••••••••••••••••••••••••••"}
            </code>
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {secret && (
                <CopyableCell
                  value={secret}
                  display=""
                  tooltipLabel="Copy secret"
                  iconSize={14}
                  iconClassName="text-muted-foreground hover:text-foreground"
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleSecret}
                disabled={loadingSecret}
                title={showSecret ? "Hide secret" : "Show secret"}
                className="h-7 w-7 p-0"
              >
                {loadingSecret ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : showSecret ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!keyData.isActive && keyData.revokedAt && keyData.revokedReason && (
        <div className="mb-6 p-3 bg-muted/50 rounded-lg border border-border/40">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Revoked:</span>{" "}
            {keyData.revokedReason}
          </p>
        </div>
      )}

      {isAdmin && keyData.isActive && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onRotate(keyData.keyId, keyData.version)}
          >
            Regenerate
          </Button>
          <Button variant="destructive" onClick={() => onRevoke(keyData.keyId)}>
            Revoke
          </Button>
        </div>
      )}
    </Card>
  );
}
